import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

const DisciplinaryRecords = ({ onClose }) => {
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [incidentDate, setIncidentDate] = useState(dayjs());
  const [disciplinaryReason, setDisciplinaryReason] = useState('');
  const [requiredActions, setRequiredActions] = useState('');
  const [templateText, setTemplateText] = useState('');

  // Fetch locations on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/locations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch locations');
        }

        const data = await response.json();
        console.log('Locations data received:', data); // Debug log
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received for locations');
        }

        setLocations(data);
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError(err.message || 'Failed to load locations');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  // Fetch employees when location changes
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!selectedLocation) {
        setEmployees([]);
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/locations/${selectedLocation}/employees`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) throw new Error('Failed to fetch employees');
        const data = await response.json();
        console.log('Employees data received:', data); // Debug log
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received for employees');
        }

        setEmployees(data);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setError('Failed to load employees');
        setEmployees([]); // Reset employees on error
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [selectedLocation]);

  const handleLocationChange = (event) => {
    const locationId = event.target.value;
    setSelectedLocation(locationId);
    setSelectedEmployee('');
    
    const location = locations.find(loc => loc?.id === locationId);
    updateTemplateText(location?.name || '', '');
  };

  const handleEmployeeChange = (event) => {
    const employeeId = event.target.value;
    setSelectedEmployee(employeeId);
    
    if (!Array.isArray(employees)) {
      console.error('Employees is not an array:', employees);
      return;
    }

    const employee = employees.find(emp => emp?.id === employeeId);
    const location = locations.find(loc => loc?.id === selectedLocation);
    
    if (employee && location) {
      const fullName = `${employee.firstName} ${employee.lastName}`;
      updateTemplateText(location.name, fullName);
    }
  };

  const updateTemplateText = (location, employee) => {
    let employeeName = employee;
    let locationName = location;

    // Only try to find employee if employees is an array
    if (Array.isArray(employees) && selectedEmployee) {
      const employeeData = employees.find(emp => emp?.id === selectedEmployee);
      if (employeeData) {
        employeeName = `${employeeData.firstName} ${employeeData.lastName}`;
      }
    }

    // Only try to find location if locations is an array
    if (Array.isArray(locations) && selectedLocation) {
      const locationData = locations.find(loc => loc?.id === selectedLocation);
      if (locationData) {
        locationName = locationData.name;
      }
    }

    const template = `Dear ${employeeName || '[Employee Name]'},

This letter serves as a formal record of disciplinary action taken on ${incidentDate ? incidentDate.format('MMMM D, YYYY') : '[Date]'} at ${locationName || '[Location Name]'}.

Reason for disciplinary action:
${disciplinaryReason || '[Reason will be inserted here]'}

Required corrective actions:
${requiredActions || '[Actions will be inserted here]'}

Please note:
1. This document will be placed in your personnel file
2. Future incidents may result in additional disciplinary action
3. You have the right to submit a written response to this notice
4. Improvement in this area must be demonstrated by [Timeline]

If you have any questions about this disciplinary action or the required corrective measures, please contact your supervisor or HR representative.

Acknowledgment:
By signing below, you confirm receipt of this disciplinary notice. Your signature does not necessarily indicate agreement with the content.

Employee Signature: ___________________ Date: ___________________
Manager Signature: ___________________ Date: ___________________

${locationName || '[Location Name]'}`; 

    setTemplateText(template);
  };

  const handleSubmit = async () => {
    if (!selectedEmployee || !disciplinaryReason || !requiredActions) {
      setError('Please fill in all required fields');
      return;
    }
    setConfirmDialog(true);
  };

  const handleConfirmSubmission = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/employees/disciplinary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employeeId: selectedEmployee,
          incidentDate: incidentDate.format('YYYY-MM-DD'),
          reason: disciplinaryReason,
          requiredActions: requiredActions,
          notice: templateText
        })
      });

      if (!response.ok) throw new Error('Failed to submit disciplinary record');

      setConfirmDialog(false);
      if (onClose) onClose();
    } catch (err) {
      console.error('Error submitting record:', err);
      setError('Failed to submit disciplinary record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: "100%", mx: 'auto', p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="h5" color="primary" gutterBottom>
          Disciplinary Record Form
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Location Dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="location-select-label">Location</InputLabel>
          <Select
            labelId="location-select-label"
            id="location-select"
            value={selectedLocation}
            onChange={handleLocationChange}
            label="Location"
            disabled={loading}
          >
            {Array.isArray(locations) && locations.length > 0 ? (
              locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  <Box>
                    <Typography>{location.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {location.employeeCount || 0} employees
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No locations available</MenuItem>
            )}
          </Select>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </FormControl>

        {/* Employee Dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            label="Employee"
            disabled={!selectedLocation || loading}
          >
            {Array.isArray(employees) && employees.length > 0 ? (
              employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  <Box>
                    <Typography>
                      {employee.firstName} {employee.lastName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {employee.position} {employee.userRole ? `(${employee.userRole})` : ''}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {!selectedLocation 
                  ? 'Select a location first' 
                  : loading 
                    ? 'Loading employees...' 
                    : 'No employees found'}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Incident Date */}
        <DatePicker
          label="Incident Date"
          value={incidentDate}
          onChange={(newValue) => {
            setIncidentDate(newValue);
            updateTemplateText();
          }}
          sx={{ width: '100%', mb: 3 }}
        />

        {/* Disciplinary Reason */}
        <TextField
          label="Reason for Disciplinary Action"
          multiline
          rows={3}
          fullWidth
          sx={{ mb: 3 }}
          value={disciplinaryReason}
          onChange={(e) => {
            setDisciplinaryReason(e.target.value);
            updateTemplateText();
          }}
        />

        {/* Required Actions */}
        <TextField
          label="Required Corrective Actions"
          multiline
          rows={3}
          fullWidth
          sx={{ mb: 3 }}
          value={requiredActions}
          onChange={(e) => {
            setRequiredActions(e.target.value);
            updateTemplateText();
          }}
        />

        {/* Notice Preview */}
        <TextField
          label="Disciplinary Notice Preview"
          multiline
          rows={12}
          fullWidth
          value={templateText}
          onChange={(e) => setTemplateText(e.target.value)}
          sx={{ mb: 3 }}
        />

        {/* Submit Button */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={!selectedEmployee || !disciplinaryReason || !requiredActions}
          >
            Submit Disciplinary Record
          </Button>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
          <DialogTitle>Confirm Disciplinary Record</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to submit this disciplinary record for{' '}
              {Array.isArray(employees) && employees.find(emp => emp?.id === selectedEmployee)?.firstName}{' '}
              {Array.isArray(employees) && employees.find(emp => emp?.id === selectedEmployee)?.lastName}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmSubmission} color="primary" variant="contained">
              Confirm Submission
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DisciplinaryRecords;